import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import { Button, Modal, SearchInput } from '@forma/forma-ui-kit';
import CreateEditProperty from '../CreateEditProperty';

import { IProductPropertiesItem } from 'interfaces/products.interface';

import styles from './select-properties-modal.module.css';

interface SelectPropertiesModalProps {
  open: boolean,
  onClose: (open: false) => void,
  properties?: IProductPropertiesItem[],
  selected: string[],
  onChangeSelected: (selected: string[]) => void,
  onCreate?: (data: Omit<IProductPropertiesItem, 'id'>) => Promise<string | null>
  onEdit?: (data: IProductPropertiesItem) => Promise<string | null>,
  onRemove?: (id: string) => void,
}

const SelectPropertiesModal = ({
  open, onClose, properties, selected, onChangeSelected, onCreate, onEdit, onRemove
}: SelectPropertiesModalProps) => {
  const { t } = useTranslation();
  const [editId, setEditId] = useState<string | null>(null);
  const editColumn = properties?.find(({ id }) => editId === id);
  const [isEditOpen, setEditOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    setSearchValue('');
  }, [open]);

  const filteredProperties = properties?.filter(({ name }) => name.toLowerCase().includes(searchValue.toLowerCase()));

  const handleClickButton = (id: string) => {
    if (selected.includes(id)) onChangeSelected(selected.filter(item => item !== id));
    else onChangeSelected([...selected, id]);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('add_data')}
      size="medium"
    >
      <SearchInput placeholder={t('enter_variable_name')} onSearch={setSearchValue} />
      <div className={styles.dropdownSwitches}>
        {filteredProperties?.map(({ id, name }) => (
          <Button
            viewStyle="secondary"
            className={styles.button}
            iconClassName={styles.buttonIcon}
            onClick={() => handleClickButton(id)}
            reversed
            icon={
              <>
                <ReactSVG
                  src="/icons/edit.svg"
                  wrapper="span"
                  className={styles.iconEdit}
                  onClick={e => { e.preventDefault(); e.stopPropagation(); setEditId(id); setEditOpen(true); }}
                />
                <ReactSVG src="/icons/checked.svg" wrapper="span" className={classNames(styles.iconChecked, selected.includes(id) && styles.checked)} />
              </>
            }
            key={id}
          >
            {name}
          </Button>
        ))}
      </div>

      <Button
        onClick={() => { setEditOpen(true); setEditId(null); }}
        fullWidth
      >
        {t('create_property')}
      </Button>

      <CreateEditProperty
        open={isEditOpen}
        onClose={setEditOpen}
        data={editColumn && { id: editColumn.id, name: editColumn.name, type: editColumn.type }}
        onAdd={onCreate}
        onEdit={onEdit}
        onRemove={onRemove}
      />
    </Modal>
  );
};

export default SelectPropertiesModal;
